import React from 'react'
import { useSelector } from 'react-redux'
import { usePathParam } from '@chilecompra/react-kit'
import { Skeleton, Table, TableRow, Toast, Typography } from '@chilecompra/react-kit/components'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import TablePlaceholder from '../../components/Placeholders/TablePlaceholder'
import { PUBLIC_URL, REACT_APP_BASE_URL } from '../../config/settings/environment'
import { PATH_PARAM } from '../../config/settings/regexes'
import routes from '../../config/settings/routes'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  Content,
  FlexRow,
  InfoIcon,
  Link,
  LinkInternal,
  Separator,
  Strong,
  TableCell,
  Tooltip,
  TooltipIcon
} from './RelatedCompanySection.styles'

/**
 * The RelatedCompanySection's container.
 */
const RelatedCompanySection = () => {
  const { userType } = useAuthorization()
  const dni = usePathParam('rut')

  const { sellerType } = useSelector(state => state.basicSellerInformation)
  const { error, hasAffidavit, isForcedAffidavit, loading, relatedCompanies } = useSelector(
    state => state.relatedCompanies
  )
  const { error: summaryError } = useSelector(state => state.summarySection)

  if (error?.reason === 'FORBIDDEN_ERROR' || sellerType === 'natural' || summaryError?.reason === 'FORBIDDEN_ERROR') {
    return null
  }

  return (
    <Content>
      <FlexRow>
        <Typography fontWeight="bold" variant="h4">
          Empresas relacionadas
        </Typography>

        <Tooltip
          content={
            <Typography color="white" variant="subtitle1">
              Vínculos que sugieren actuación económica y financiera por intereses comunes con otras empresas. Se
              refiere a controladores, empresas matrices, coligantes, coligados, filiales u otros.
            </Typography>
          }
        >
          <TooltipIcon>
            <InfoIcon />
          </TooltipIcon>
        </Tooltip>
      </FlexRow>

      {loading && (
        <>
          <Skeleton margin="0 0 8px" width="100%" />
          <TablePlaceholder margin="24px 0 0" />
        </>
      )}

      {error && !loading && !relatedCompanies && (
        <Toast
          border
          margin="24px 0 0"
          severity="error"
          textDirection="vertical"
          title="Lo sentimos, estamos presentando problemas con nuestro servidor"
        >
          En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
          más.
        </Toast>
      )}

      {(!error || !!relatedCompanies) && !loading && (
        <>
          <Typography padding="0 0 8px" variant="subtitle">
            Fuente: Declaración Jurada del Registro de Proveedores ingresada por el proveedor en Mercado Público
          </Typography>

          {userType === 'Publico' && (
            <>
              {hasAffidavit ? (
                <>
                  {isForcedAffidavit ? (
                    <>
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        En estos momentos no podemos disponer de forma pública la información de esta empresa
                        proveedora.
                      </Typography>

                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Si eres comprador, o perteneces a esta empresa, puedes acceder a esta información iniciando
                        sesión en Mercado Público e ingresando a la ficha privada del proveedor.
                      </Typography>
                    </>
                  ) : (
                    relatedCompanies?.length === 0 && (
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Esta empresa proveedora ha declarado que <Strong>no posee</Strong> vínculos de actuación
                        económica y financiera con otras empresas
                      </Typography>
                    )
                  )}
                </>
              ) : (
                <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                  Esta empresa proveedora no posee información disponible sobre empresas relacionadas.
                </Typography>
              )}
            </>
          )}

          {userType !== 'Publico' && (
            <>
              {hasAffidavit ? (
                <>
                  {isForcedAffidavit ? (
                    <Toast margin="18px 0 0" severity="info">
                      La información de empresas relacionadas se muestra para todas las empresas proveedoras que hayan
                      realizado su Declaración Jurada del Registro de Proveedores a través del sistema. En caso que la
                      declaración haya sido realizada de forma manual, podrías encontrar dicha información en la sección{' '}
                      <LinkInternal to={routes.documents.replace(PATH_PARAM, dni)}>Documentos</LinkInternal>.
                    </Toast>
                  ) : (
                    relatedCompanies?.length === 0 && (
                      <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                        Esta empresa proveedora ha declarado que <Strong>no posee</Strong> vínculos de actuación
                        económica y financiera con otras empresas
                      </Typography>
                    )
                  )}
                </>
              ) : (
                <Typography color="black3" fontWeight="regular" margin="18px 0" variant="body2">
                  Debido a que la empresa proveedora no cuenta con su Declaración Jurada del Registro de Proveedores
                  firmada, no hay información disponible sobre Empresas relacionadas.
                </Typography>
              )}
            </>
          )}

          {hasAffidavit && relatedCompanies?.length > 0 && (
            <>
              <Table
                disableLatestBottomBorder
                accessibility={{ label: 'Detalle Socios y Sociedades' }}
                margin="24px 0 0"
              >
                <TableRow>
                  <TableCell>
                    <Typography fontWeight="bold" variant="body1">
                      Nombre
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight="bold" variant="body1">
                      Rut
                    </Typography>
                  </TableCell>

                  <TableCell />

                  <TableCell alignment="left" width="220px">
                    <Typography fontWeight="bold" variant="body1">
                      Relación con la empresa
                    </Typography>
                  </TableCell>
                </TableRow>

                {relatedCompanies?.map(beneficiary => (
                  <TableRow key={beneficiary.name}>
                    <TableCell>
                      <ProtectByProfile rule={userType => userType === 'Comprador'}>
                        {beneficiary.isNaturalPerson && !beneficiary.isForeign && beneficiary.name}

                        {(!beneficiary.isNaturalPerson || beneficiary.isForeign) && beneficiary.dni && (
                          <Link href={`${REACT_APP_BASE_URL}${PUBLIC_URL}/${beneficiary.dni}`}>{beneficiary.name}</Link>
                        )}
                      </ProtectByProfile>

                      <ProtectByProfile rule={userType => userType !== 'Comprador'}>
                        {beneficiary.name}
                      </ProtectByProfile>
                    </TableCell>

                    <TableCell alignment="left" width="137px">
                      {(!beneficiary.dni || beneficiary.esDesdeAdministrador || beneficiary.isNaturalPerson) &&
                      userType === 'Publico'
                        ? '-'
                        : beneficiary.dni}
                    </TableCell>

                    <TableCell alignment="left" width="137px">
                      {beneficiary.isFinalBeneficiary ? 'Beneficiario Final' : ''}
                    </TableCell>

                    <TableCell alignment="left" width="137px">
                      {beneficiary.relation}
                    </TableCell>
                  </TableRow>
                ))}
              </Table>

              <ProtectByProfile rule={userType => userType === 'Comprador'}>
                <Toast margin="24px 0 0" severity="info">
                  Puedes revisar la ficha de cada proveedor Persona Jurídica pinchando su razón social o en su RUT.
                </Toast>
              </ProtectByProfile>
            </>
          )}
          <Separator />
        </>
      )}
    </Content>
  )
}

export default RelatedCompanySection
